import React, { useState, useEffect, useRef } from "react";
import { LoadingSkeleton } from "../payments_tile/LoadingSkeleton";

const TERMINAL_STATES = ["SUCCEEDED", "FAILED", "ERROR", "NOT_FOUND"];
const DEFAULT_POLL_DURATION = 3500;
const LOG_PREFIX = "[BalancePoller]";

const serverDataElement = document.getElementById("_server_data_modal");
const serverData = cedar.safelyParseJSONFromString(serverDataElement?.textContent || "");
const epicPollDuration = serverData.pull_and_update_external_invoice_balances_timeout;
const pollDuration =
    epicPollDuration && epicPollDuration >= 1000 ? epicPollDuration : DEFAULT_POLL_DURATION;

const onSuccess = (jobId: any, data: any) => {
    console.info(LOG_PREFIX, "Balance update succeeded", {
        jobId,
        status: data.status,
        hasBalanceDetails: !!data.balance_details,
    });
};

export function RealTimeBalanceStatusPoller() {
    const [isLoading, setIsLoading] = useState(true);
    const [shouldPoll, setShouldPoll] = useState(true);
    const isPollingRef = useRef(false);

    const jobId = (() => {
        try {
            const serverDataElement = document.getElementById("_server_data_modal");
            if (!serverDataElement?.textContent) {
                return null;
            }

            const serverData = cedar.safelyParseJSONFromString(
                serverDataElement?.textContent || "",
            );
            if (serverData.error) {
                console.error(
                    LOG_PREFIX,
                    "Error parsing server data for job ID:",
                    serverData.error,
                );
                return null;
            }
            return serverData.balance_update_job_id;
        } catch (e) {
            console.error(LOG_PREFIX, "Error getting job ID:", e);
            return null;
        }
    })();

    useEffect(() => {
        if (!shouldPoll) {
            return;
        }

        // If no jobId, set up timeout to hide loading skeleton after pollDuration
        if (!jobId) {
            const timeoutId = setTimeout(() => {
                setIsLoading(false);
                setShouldPoll(false);
            }, pollDuration);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        let statusPollInterval: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const cleanup = () => {
            if (statusPollInterval) {
                clearInterval(statusPollInterval);
                statusPollInterval = null;
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        const pollBalanceStatus = async () => {
            if (isPollingRef.current) {
                return;
            }

            isPollingRef.current = true;
            try {
                setIsLoading(true);
                const params = new URLSearchParams(window.location.search);
                const userId = params.get("user");
                const url = `/api/patient/balance-update-status/${jobId}/${userId}/`;
                const response = await fetch(url);

                if (response.ok) {
                    const data = await response.json();

                    if (TERMINAL_STATES.includes(data.status)) {
                        if (data.status === "SUCCEEDED") {
                            onSuccess(jobId, data);
                        } else {
                            console.warn(LOG_PREFIX, "Balance update ended with status", {
                                jobId,
                                status: data.status,
                                message: data.message,
                            });
                        }
                        setIsLoading(false);
                        setShouldPoll(false);
                        cleanup();
                    }
                } else {
                    const responseText = await response.text();
                    console.error(LOG_PREFIX, "Balance status request failed", {
                        jobId,
                        status: response.status,
                        message: responseText,
                    });
                    setIsLoading(false);
                    setShouldPoll(false);
                    if (statusPollInterval) {
                        clearInterval(statusPollInterval);
                        statusPollInterval = null;
                    }
                }
            } catch (e) {
                console.error(LOG_PREFIX, "Error polling balance status:", e);
                setIsLoading(false);
                setShouldPoll(false);
                if (statusPollInterval) {
                    clearInterval(statusPollInterval);
                    statusPollInterval = null;
                }
            } finally {
                isPollingRef.current = false;
            }
        };

        pollBalanceStatus();
        statusPollInterval = setInterval(pollBalanceStatus, 300);

        timeoutId = setTimeout(() => {
            console.info(LOG_PREFIX, "Max polling duration reached");
            setIsLoading(false);
            setShouldPoll(false);
            cleanup();
        }, pollDuration);

        return cleanup;
    }, [shouldPoll, jobId]);

    if (isLoading) {
        document.querySelectorAll(".payment-tile-container").forEach((tile) => {
            (tile as HTMLElement).style.display = "none";
        });
        return <LoadingSkeleton showCtaBar={true} />;
    } else {
        document.querySelectorAll(".payment-tile-container").forEach((tile) => {
            (tile as HTMLElement).style.display = "block";
        });
    }
    return null;
}
